import { FC } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { generalEnum } from '../../../../utils/generalEnum';
import TemplateDetails from '../TemplateDetails';
import { DetailsRow } from 'lib/ClientDataDetails/DetailsRow';
import { ClientDetailsConstructionDepot } from 'types/ClientDetails/ClientDetailsConstructionDepot';
import { ClientDetailsSustainabilityDepot } from 'types/ClientDetails/ClientDetailsSustainabilityDepot';
import { ConstructionDepotTypeEnum } from 'types/ClientDetails/Enums/ConstructionDepotTypeEnum';

type ConstructionDepotDetailsProps = {
  constructionDepot?: ClientDetailsConstructionDepot;
  sustainabilityDepot?: ClientDetailsSustainabilityDepot;
};

const ConstructionDepotDetails: FC<ConstructionDepotDetailsProps> = ({
  constructionDepot,
  sustainabilityDepot,
}) => {
  const { t } = useTranslation();
  const translationsRoot = 'clients.clientData';

  if (!constructionDepot && !sustainabilityDepot) {
    return null;
  }

  const withdrawnConstruction =
    (constructionDepot?.amount ?? 0) - (constructionDepot?.balance ?? 0);
  const withdrawnSustainability =
    (sustainabilityDepot?.amount ?? 0) - (sustainabilityDepot?.balance ?? 0);

  return (
    <Grid container data-testid="construction-depot-container">
      {!constructionDepot || !Object.values(constructionDepot).length ? null : (
        <Grid xs={6} item px={3} py={2}>
          <TemplateDetails
            testid="construction-depot"
            title={t(`${translationsRoot}.depotType.constructionDepot`)}
          >
            <DetailsRow
              type="date"
              label={t(`${translationsRoot}.depot.startDate`)}
              value={constructionDepot.startDate}
            />
            <DetailsRow
              type="date"
              label={t(`${translationsRoot}.depot.endDate`)}
              value={constructionDepot.endDate}
            />
            <DetailsRow
              type="currency"
              label={t(`${translationsRoot}.depot.amount`)}
              value={constructionDepot.amount}
            />
            <DetailsRow
              type="currency"
              label={t(`${translationsRoot}.depot.balance`)}
              value={constructionDepot.balance}
            />
            <DetailsRow
              type="percentage"
              label={t(`${translationsRoot}.depot.interestRate`)}
              value={constructionDepot.interestRate}
            />
            <DetailsRow
              type="currency"
              label={t(`${translationsRoot}.depot.withdrawn`)}
              value={withdrawnConstruction === 0 ? null : withdrawnConstruction}
            />
            <DetailsRow
              label={t(`${translationsRoot}.depot.type`)}
              value={generalEnum(
                'constructionDepotType',
                constructionDepot.type,
                ConstructionDepotTypeEnum
              )}
            />
          </TemplateDetails>
        </Grid>
      )}

      {!sustainabilityDepot || !Object.values(sustainabilityDepot).length ? null : (
        <Grid xs={6} item px={3} py={2}>
          <TemplateDetails
            testid="construction-depot"
            title={t(`${translationsRoot}.depotType.sustainabilityDepot`)}
          >
            <DetailsRow
              type="date"
              label={t(`${translationsRoot}.depot.startDate`)}
              value={sustainabilityDepot.startDate}
            />
            <DetailsRow
              type="currency"
              label={t(`${translationsRoot}.depot.amount`)}
              value={sustainabilityDepot.amount}
            />
            <DetailsRow
              type="currency"
              label={t(`${translationsRoot}.depot.balance`)}
              value={sustainabilityDepot.balance}
            />
            <DetailsRow
              type="percentage"
              label={t(`${translationsRoot}.depot.interestRate`)}
              value={sustainabilityDepot.interestRate}
            />
            <DetailsRow
              type="currency"
              label={t(`${translationsRoot}.depot.withdrawn`)}
              value={withdrawnSustainability === 0 ? null : withdrawnSustainability}
            />
          </TemplateDetails>
        </Grid>
      )}
    </Grid>
  );
};

export default ConstructionDepotDetails;
